<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref } from 'vue';
import { useQuasar } from 'quasar';

import type { UserPermissionDto } from '@/services/types/api.types';
import { useAuthStore } from '@/stores';

import ReorderableTable from '@/components/ReorderableTable.vue';
import UserPermissionAddDialog from './dialogs/UserPermissionAddDialog.vue';

const { t } = useI18n();
const $q = useQuasar();
const authStore = useAuthStore();

const props = defineProps<{
  fetchUserPermissions: () => Promise<UserPermissionDto[]>;
  addUserPermission: (email: string, role: number) => Promise<boolean>;
  deleteUserPermission: (data: UserPermissionDto) => Promise<void>;
  roles?: number[];
}>();

const data = ref<UserPermissionDto[]>([]);

const columns = computed(() => [
  {
    name: 'email',
    label: t('community.admins.email'),
    align: 'left',
    field: (row: UserPermissionDto) => row.User.email,
  },
]);

const actionMenuItems = computed(() => [
  {
    action: 'delete',
    label: t('global.label.delete'),
    disabled: (row: any) => row.user_id === authStore.user?.id,
  },
]);

onMounted(async () => {
  data.value = await props.fetchUserPermissions();
});

const handleAddRow = () => {
  $q.dialog({
    component: UserPermissionAddDialog,
    componentProps: {
      createUserPermission: props.addUserPermission,
    },
  }).onOk(async () => {
    data.value = await props.fetchUserPermissions();
  });
};

const handleActions = (action: any) => {
  if (action.action === 'delete') {
    $q.dialog({
      title: t('global.dialogs.deleteUserPermissionTitle'),
      message: t('global.dialogs.deleteUserPermissionMessage'),
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      await props.deleteUserPermission(action.payload.row);
      data.value = await props.fetchUserPermissions();
    });
  }
};

const filterRoles = (rows: UserPermissionDto[]) => {
  if (!props.roles) return true;
  return rows.filter((row) => props.roles?.includes(row.role) ?? true);
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="data"
      :columns="columns"
      :title="t('user_permissions.roles.editor', 2)"
      itemKey="id"
      :disable-orderable="true"
      :disable-delete-button="true"
      :filter="roles"
      :filter-method="filterRoles"
      :action-menu-items="actionMenuItems"
      @add-row="handleAddRow"
      @actions="handleActions"
    />
  </div>
</template>
